/**
 * Feed Ordering Utilities
 * 
 * Shared algorithms for seen-demotion ordering and projects feed layering.
 * Used by both main feed and projects feed.
 */

/**
 * Parse various timestamp formats to milliseconds
 * @param {*} t - Firestore Timestamp, Date, or ISO string
 * @returns {number} - Milliseconds since epoch
 */
export const parseTimestamp = (t) => {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (t instanceof Date) return t.getTime();
    // Fix edge case: check if toDate is a function before calling
    if (typeof t?.toDate === 'function') {
        const date = t.toDate();
        return date ? date.getTime() : 0;
    }
    if (t?.seconds) return t.seconds * 1000; // Firestore-like
    try { return new Date(t).getTime(); } catch { return 0; }
};

/**
 * Seen-Demotion Algorithm
 * 
 * Orders feed items with unseen items first (chronological),
 * then seen items (most recently seen first).
 * 
 * If priorityPostId is provided, that item is placed at index 0
 * (used for trending post click feature).
 * 
 * @param {Array} items - Feed items with id, timestamp, and optional seenAt
 * @param {Map|Object} seenHistory - itemId -> { seenAt, seenBatch }
 * @param {string} [priorityPostId] - Optional post ID to place at top of feed
 * @returns {Array} - Ordered items
 */
export const applySeenDemotion = (items, seenHistory = {}, priorityPostId = null) => {
    if (!items?.length) return [];

    // Convert seenHistory to Map if it's an object
    const seenMap = seenHistory instanceof Map
        ? seenHistory
        : new Map(Object.entries(seenHistory || {}));

    // Extract priority item if specified
    let priorityItem = null;
    let remainingItems = items;

    if (priorityPostId) {
        const priorityIndex = items.findIndex(item => {
            const itemId = item.id || item.postId || item.projectId;
            return itemId === priorityPostId;
        });

        if (priorityIndex !== -1) {
            priorityItem = items[priorityIndex];
            remainingItems = [
                ...items.slice(0, priorityIndex),
                ...items.slice(priorityIndex + 1)
            ];
        }
    }

    // Partition remaining items into seen/unseen
    const unseen = [];
    const seen = [];

    remainingItems.forEach(item => {
        const itemId = item.id || item.postId || item.projectId;
        const seenData = seenMap.get(itemId);

        if (seenData) {
            seen.push({
                ...item,
                _seenAt: parseTimestamp(seenData.seenAt),
                _seenBatch: seenData.seenBatch || 0
            });
        } else {
            unseen.push(item);
        }
    });

    // Sort unseen by timestamp DESC (newest first)
    unseen.sort((a, b) => {
        const aTime = parseTimestamp(a.timestamp || a.createdAt || a.time);
        const bTime = parseTimestamp(b.timestamp || b.createdAt || b.time);
        return bTime - aTime;
    });

    // Sort seen by seenBatch DESC, then seenAt DESC (most recently seen first)
    seen.sort((a, b) => {
        if (a._seenBatch !== b._seenBatch) {
            return b._seenBatch - a._seenBatch;
        }
        return b._seenAt - a._seenAt;
    });

    // Clean up temporary fields using immutable destructuring
    const cleanedSeen = seen.map(({ _seenAt, _seenBatch, ...rest }) => rest);

    // Combine: priority item first (if any), then unseen, then seen
    const orderedItems = [...unseen, ...cleanedSeen];

    if (priorityItem) {
        return [priorityItem, ...orderedItems];
    }

    return orderedItems;
};

/**
 * Projects Feed Layering
 * 
 * Orders projects feed with:
 * 1. Connected projects (user is creator or team member) - first
 * 2. Recommended/followed projects - second
 * 3. Old/unrelated projects - last
 * 
 * Within each layer, applies seen-demotion.
 * 
 * @param {Array} projects - Project items
 * @param {string} userId - Current user's ID
 * @param {Array} followedProjectIds - IDs of projects user follows
 * @param {Map|Object} seenHistory - itemId -> { seenAt, seenBatch }
 * @returns {Array} - Layered and ordered projects
 */
export const layerProjectsFeed = (projects, userId, followedProjectIds = [], seenHistory = {}) => {
    if (!projects?.length || !userId) return projects || [];

    const followedSet = new Set(followedProjectIds);

    // Connected = user is creator OR in teamMemberIds
    const connected = projects.filter(p =>
        p.creatorId === userId ||
        (p.teamMemberIds || []).includes(userId)
    );

    const connectedIds = new Set(connected.map(p => p.id));

    // Recommended = user follows this project, but not connected
    const recommended = projects.filter(p =>
        followedSet.has(p.id) && !connectedIds.has(p.id)
    );

    const recommendedIds = new Set(recommended.map(p => p.id));

    // Everything else
    const other = projects.filter(p =>
        !connectedIds.has(p.id) && !recommendedIds.has(p.id)
    );

    // Apply seen-demotion within each layer
    return [
        ...applySeenDemotion(connected, seenHistory),
        ...applySeenDemotion(recommended, seenHistory),
        ...applySeenDemotion(other, seenHistory)
    ];
};
